/**
 * Mega Trainer Service
 * Comprehensive sales training system combining Lumo method with SPIN, MEDDIC, and Sandler
 * Provides adaptive learning paths, structured drills, and progressive coaching
 */

import { supabase } from '../lib/supabase';

export const megaTrainerService = {
  /**
   * TRAINING CURRICULUM DEFINITIONS
   */

  TRAINING_MODULES: {
    lumo_core: {
      id: 'lumo_core',
      name: 'Lumo Core Method',
      description: 'Master the 5 Lumo principles: even-tone authenticity, micro-probes, neutral confidence, stacked objection handling, and continuous improvement',
      pillars: [
        'even_tone_authenticity',
        'micro_probes',
        'neutral_confidence',
        'stacked_objections',
        'improvement_loop'
      ],
      difficulty: 'all',
      duration: '4 weeks',
      lessons: 12,
      certificationScore: 85
    },
    spin_selling: {
      id: 'spin_selling',
      name: 'SPIN Selling Framework',
      description: 'Neil Rackham\'s Situation-Problem-Implication-Need-Payoff framework for consultative selling',
      pillars: [
        'situation_questions',
        'problem_probes',
        'implication_building',
        'need_identification',
        'payoff_establishment'
      ],
      difficulty: 'intermediate',
      duration: '3 weeks',
      lessons: 9,
      certificationScore: 80
    },
    meddic: {
      id: 'meddic',
      name: 'MEDDIC Sales Process',
      description: 'Metrics-Economic-Decision-Decision Process-Identify Pain-Champion framework for enterprise deals',
      pillars: [
        'metrics_definition',
        'economic_buyer',
        'decision_criteria',
        'decision_process',
        'identify_pain',
        'champion_building'
      ],
      difficulty: 'advanced',
      duration: '4 weeks',
      lessons: 12,
      certificationScore: 85
    },
    sandler: {
      id: 'sandler',
      name: 'Sandler Sales Method',
      description: 'David Sandler\'s emotional intelligence-driven approach emphasizing qualification and neutrality',
      pillars: [
        'bonding_rapport',
        'up_front_contracts',
        'qualification',
        'pain_discovery',
        'problem_priority',
        'emotional_commitment',
        'post_settlement'
      ],
      difficulty: 'intermediate',
      duration: '4 weeks',
      lessons: 14,
      certificationScore: 85
    },
    challenger: {
      id: 'challenger',
      name: 'Challenger Sale',
      description: 'Gartner\'s insight-driven approach: teach, tailor, take control of complex B2B sales',
      pillars: [
        'insight_teaching',
        'hypothesis_crafting',
        'value_instigation',
        'commercial_insight',
        'solution_tailoring'
      ],
      difficulty: 'advanced',
      duration: '3 weeks',
      lessons: 9,
      certificationScore: 80
    },
    // New: NEPQ
    nepq: {
      id: 'nepq',
      name: 'NEPQ – Neuro-Emotional Persuasion Questions',
      description: 'Precision questions that reduce resistance, uncover motives, and increase buying desire',
      pillars: [
        'connection_phase',
        'situation_phase',
        'problem_awareness',
        'consequence_questions',
        'commitment_questions'
      ],
      difficulty: 'intermediate',
      duration: '3 weeks',
      lessons: 5,
      certificationScore: 80
    },
    // New: GAP Selling
    gap_selling: {
      id: 'gap_selling',
      name: 'GAP Selling',
      description: 'Drive urgency by quantifying the gap between current state and desired future state',
      pillars: [
        'current_state',
        'future_state',
        'gap_quantification',
        'root_cause',
        'business_impact'
      ],
      difficulty: 'intermediate',
      duration: '3 weeks',
      lessons: 5,
      certificationScore: 80
    },
    // New: Straight Line – Tonality & Certainty
    straight_line: {
      id: 'straight_line',
      name: 'Straight Line (Tonality & Certainty)',
      description: 'Control the sale by mastering tonality and driving certainty in three areas',
      pillars: [
        'tonality_mastery',
        'certainty_framework'
      ],
      difficulty: 'beginner',
      duration: '2 weeks',
      lessons: 2,
      certificationScore: 75
    },
    // New: Value Selling
    value_selling: {
      id: 'value_selling',
      name: 'Value Selling',
      description: 'Win on value, not price: quantify outcomes and tie to business drivers',
      pillars: [
        'value_hypothesis',
        'stakeholder_value',
        'quantify_value',
        'proof_and_risk',
        'business_case'
      ],
      difficulty: 'intermediate',
      duration: '3 weeks',
      lessons: 5,
      certificationScore: 80
    },
    objection_mastery: {
      id: 'objection_mastery',
      name: 'Advanced Objection Mastery',
      description: 'Comprehensive objection handling, stacking, and real-world scenario training',
      pillars: [
        'objection_types',
        'acknowledge_pivot',
        'frame_flipping',
        'stacked_handling',
        'persona_adaptation'
      ],
      difficulty: 'all',
      duration: '6 weeks',
      lessons: 18,
      certificationScore: 85
    }
  },

  /**
   * Get best practice score for a lesson from Supabase (fallback handled by caller)
   */
  async getLessonPracticeScore(userId, moduleId, lessonNumber) {
    try {
      const { data, error } = await supabase
        ?.from('trainer_practice_scores')
        ?.select('best_score')
        ?.eq('user_id', userId)
        ?.eq('module_id', moduleId)
        ?.eq('lesson_number', lessonNumber)
        ?.single();

      if (error && error.code !== 'PGRST116' && error.code !== 'PGRST205') throw error;
      return Number(data?.best_score) || 0;
    } catch (err) {
      console.error('Error fetching practice score:', err);
      return 0;
    }
  },

  /**
   * Upsert best practice score for a lesson
   */
  async setLessonPracticeScore(userId, moduleId, lessonNumber, score) {
    try {
      const bestScore = Number(score) || 0;
      const { error } = await supabase
        ?.from('trainer_practice_scores')
        ?.upsert({
          user_id: userId,
          module_id: moduleId,
          lesson_number: lessonNumber,
          best_score: bestScore,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;
      return bestScore;
    } catch (err) {
      console.error('Error saving practice score:', err);
      return 0;
    }
  },

  /**
   * Get user's recommended learning path based on profile
   */
  async getRecommendedPath(userId) {
    try {
      const { data: profile, error } = await supabase
        ?.from('user_profiles')
        ?.select('skill_level, industry, role')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;

      const paths = {
        beginner: [
          this.TRAINING_MODULES.lumo_core,
          this.TRAINING_MODULES.spin_selling,
          this.TRAINING_MODULES.objection_mastery
        ],
        intermediate: [
          this.TRAINING_MODULES.lumo_core,
          this.TRAINING_MODULES.sandler,
          this.TRAINING_MODULES.meddic,
          this.TRAINING_MODULES.objection_mastery
        ],
        advanced: [
          this.TRAINING_MODULES.lumo_core,
          this.TRAINING_MODULES.meddic,
          this.TRAINING_MODULES.challenger,
          this.TRAINING_MODULES.objection_mastery
        ]
      };

      return paths[profile?.skill_level || 'beginner'] || paths.beginner;
    } catch (error) {
      console.error('Error getting recommended path:', error);
      return Object.values(this.TRAINING_MODULES).slice(0, 3);
    }
  },

  /**
   * Get structured drill progression for an objection type
   * Returns: warm-up → core → mastery drills
   */
  async getDrillProgression(objectionType, userSkillLevel = 'intermediate') {
    try {
      const difficultyMap = {
        beginner: { min: 1, max: 2 },
        intermediate: { min: 2, max: 3 },
        advanced: { min: 3, max: 5 }
      };

      const { min, max } = difficultyMap[userSkillLevel];

      const { data: drills, error } = await supabase
        ?.from('objections')
        ?.select('*')
        ?.eq('label', objectionType)
        ?.gte('difficulty', min)
        ?.lte('difficulty', max)
        ?.order('difficulty', { ascending: true });

      if (error) throw error;

      return {
        warmup: drills?.filter(d => d?.difficulty <= min),
        core: drills?.filter(d => d?.difficulty > min && d?.difficulty < max),
        mastery: drills?.filter(d => d?.difficulty >= max)
      };
    } catch (error) {
      console.error('Error getting drill progression:', error);
      return { warmup: [], core: [], mastery: [] };
    }
  },

  /**
   * Get coaching lesson for a module pillar
   */
  async getCoachingLesson(moduleId, pillarName) {
    try {
      const { data: lesson, error } = await supabase
        ?.from('coaching_concepts')
        ?.select('*')
        ?.ilike('tags', `%${pillarName}%`)
        ?.limit(1)
        ?.single();

      if (error && error.code !== 'PGRST116') throw error;
      // Try alias mapping when exact content key is not found
      const key = this.getContentKeyForPillar(moduleId, pillarName);

      return lesson || megaTrainerService.LESSON_CONTENT[moduleId]?.[key] || {
        title: pillarName,
        summary: `Practical foundations for ${pillarName.replace(/_/g, ' ')}`,
        bullet_points: 'Clarify the objective\nAsk a targeted probe\nSummarize and set next step',
        example_script: 'I hear you. Help me understand what would make this a clear yes for you? Then we can map next steps.',
        tags: [pillarName],
        difficulty: 2
      };
    } catch (error) {
      console.error('Error getting coaching lesson:', error);
      return null;
    }
  },

  /**
   * Resolve mismatched pillar names to actual content keys
   */
  getContentKeyForPillar(moduleId, pillarName) {
    const content = this.LESSON_CONTENT?.[moduleId] || {};
    if (content[pillarName]) return pillarName;

    const aliasMap = {
      challenger: {
        insight_teaching: 'teaching',
        hypothesis_crafting: 'teaching',
        value_instigation: 'teaching',
        commercial_insight: 'teaching',
        solution_tailoring: 'tailoring',
        taking_control: 'taking_control'
      },
      objection_mastery: {
        objection_types: 'price_objections',
        acknowledge_pivot: 'timing_objections',
        frame_flipping: 'competitor_objections',
        stacked_handling: 'budget_objections',
        persona_adaptation: 'fit_objections'
      }
    };

    const moduleAliases = aliasMap[moduleId] || {};
    const mapped = moduleAliases[pillarName];
    if (mapped && content[mapped]) return mapped;

    // Fuzzy: try to find a content key containing a stem of the pillar
    const stem = String(pillarName).replace(/_/g, ' ').toLowerCase();
    const candidate = Object.keys(content).find((k) => stem.includes(k.replace(/_/g, ' ').toLowerCase()) || k.replace(/_/g, ' ').toLowerCase().includes(stem));
    if (candidate) return candidate;

    // Fallback to first available content key
    return Object.keys(content)[0];
  },

  /**
   * Get module progress for user
   */
  async getModuleProgress(userId, moduleId) {
    try {
      const { data: progress, error } = await supabase
        ?.from('user_training_progress')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('module_id', moduleId)
        ?.single();

      if (error && error.code !== 'PGRST116') throw error;

      return progress || {
        lessonsCompleted: 0,
        lessonsTotal: this.TRAINING_MODULES[moduleId]?.lessons || 12,
        currentStreak: 0,
        certificateEarned: false,
        progressPercent: 0
      };
    } catch (error) {
      console.error('Error getting module progress:', error);
      return {
        lessonsCompleted: 0,
        lessonsTotal: 12,
        currentStreak: 0,
        certificateEarned: false,
        progressPercent: 0
      };
    }
  },

  /**
   * Get real-world scenarios for a module from trainer_scenarios table
   * Returns all scenarios for module, with lesson_number alignment
   */
  async getScenarios(moduleId, lessonNumber = null) {
    try {
      let query = supabase
        ?.from('trainer_scenarios')
        ?.select('*')
        ?.eq('module_id', moduleId)
        ?.order('lesson_number', { ascending: true });

      const { data: scenarios, error } = await query;

      if (error) throw error;

      // If lessonNumber provided, return that specific scenario; otherwise return all
      if (lessonNumber) {
        const specific = scenarios?.find(s => s.lesson_number === Number(lessonNumber));
        return specific ? [specific] : [];
      }

      return scenarios || [];
    } catch (error) {
      console.error('Error getting trainer scenarios:', error);
      return [];
    }
  },

  /**
   * Get scenario by module and lesson number (primary lookup)
   */
  async getScenarioForLesson(moduleId, lessonNumber) {
    try {
      const { data: scenario, error } = await supabase
        ?.from('trainer_scenarios')
        ?.select('*')
        ?.eq('module_id', moduleId)
        ?.eq('lesson_number', Number(lessonNumber))
        ?.single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows found
      return scenario || null;
    } catch (error) {
      console.error('Error getting scenario for lesson:', error);
      return null;
    }
  },

  /**
   * Get adaptive recommendation based on user's weak spots
   */
  async getAdaptiveRecommendation(userId) {
    try {
      // Get user's recent practice attempts
      const { data: attempts, error: attemptsError } = await supabase
        ?.from('user_objection_attempts')
        ?.select('objection_id, lumo_score')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(20);

      if (attemptsError) throw attemptsError;

      if (!attempts || attempts.length === 0) {
        return {
          recommendation: 'Start with Lumo Core Method to build fundamentals',
          moduleId: 'lumo_core',
          reason: 'No practice history found'
        };
      }

      // Find weakest areas
      const avgScore = attempts.reduce((sum, a) => sum + (a?.lumo_score || 0), 0) / attempts.length;

      if (avgScore < 70) {
        return {
          recommendation: 'Focus on fundamentals with Lumo Core Method',
          moduleId: 'lumo_core',
          reason: 'Average score below 70. Master basics first.',
          improvement: `Current avg: ${Math.round(avgScore)}%`
        };
      } else if (avgScore < 80) {
        return {
          recommendation: 'Build objection handling mastery',
          moduleId: 'objection_mastery',
          reason: 'Good progress! Time to tackle harder objections.',
          improvement: `Current avg: ${Math.round(avgScore)}%`
        };
      } else {
        return {
          recommendation: 'Advance to enterprise sales frameworks',
          moduleId: 'meddic',
          reason: 'Strong fundamentals. Ready for advanced frameworks.',
          improvement: `Current avg: ${Math.round(avgScore)}%`
        };
      }
    } catch (error) {
      console.error('Error getting adaptive recommendation:', error);
      return {
        recommendation: 'Start with Lumo Core Method',
        moduleId: 'lumo_core',
        reason: 'Default path'
      };
    }
  },

  /**
   * Track module lesson completion
   */
  async completeLesson(userId, moduleId, lessonNumber) {
    try {
      const { data: progress, error: fetchError } = await supabase
        ?.from('user_training_progress')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('module_id', moduleId)
        ?.single();

      if (fetchError && fetchError.code === 'PGRST116') {
        // Create new progress record
        const { data: newProgress, error: createError } = await supabase
          ?.from('user_training_progress')
          ?.insert({
            user_id: userId,
            module_id: moduleId,
            lessons_completed: 1,
            current_streak: 1,
            last_lesson_date: new Date().toISOString()
          })
          ?.select()
          ?.single();

        if (createError) throw createError;
        return newProgress;
      }

      if (fetchError) throw fetchError;

      // Update existing progress
      const { data: updated, error: updateError } = await supabase
        ?.from('user_training_progress')
        ?.update({
          lessons_completed: (progress?.lessons_completed || 0) + 1,
          current_streak: (progress?.current_streak || 0) + 1,
          last_lesson_date: new Date().toISOString()
        })
        ?.eq('user_id', userId)
        ?.eq('module_id', moduleId)
        ?.select()
        ?.single();

      if (updateError) throw updateError;
      return updated;
    } catch (error) {
      console.error('Error completing lesson:', error);
      return null;
    }
  },

  /**
   * Check if user qualifies for module certification
   */
  async checkCertification(userId, moduleId) {
    try {
      const module = this.TRAINING_MODULES[moduleId];
      if (!module) throw new Error('Module not found');

      // Get module progress
      const { data: progress, error: progressError } = await supabase
        ?.from('user_training_progress')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('module_id', moduleId)
        ?.single();

      if (progressError) throw progressError;

      // Get average score on module-specific drills
      const { data: attempts, error: attemptsError } = await supabase
        ?.from('user_objection_attempts')
        ?.select('lumo_score')
        ?.eq('user_id', userId)
        ?.gte('created_at', progress?.started_at || new Date(Date.now() - 86400000 * 30))
        ?.limit(10);

      if (attemptsError) throw attemptsError;

      if (!attempts || attempts.length === 0) {
        return { qualified: false, reason: 'No practice drills completed', score: 0 };
      }

      const avgScore = attempts.reduce((sum, a) => sum + (a?.lumo_score || 0), 0) / attempts.length;
      const lessonsCompleted = progress?.lessons_completed || 0;
      const lessonsRequired = module.lessons || 12;

      const qualified = 
        avgScore >= module.certificationScore &&
        lessonsCompleted >= (lessonsRequired * 0.8); // 80% lesson completion

      return {
        qualified,
        score: Math.round(avgScore),
        lessonsCompleted,
        lessonsRequired,
        reason: qualified ? 'Certification earned!' : 
                avgScore < module.certificationScore ? `Score needs to be ${module.certificationScore}+` :
                `Complete ${Math.ceil(lessonsRequired * 0.8) - lessonsCompleted} more lessons`
      };
    } catch (error) {
      console.error('Error checking certification:', error);
      return { qualified: false, reason: 'Error checking qualification', score: 0 };
    }
  }
};

// Comprehensive lesson content for all modules and pillars
megaTrainerService.LESSON_CONTENT = {
  lumo_core: {
    even_tone_authenticity: {
      title: 'Even-Tone Authenticity',
      summary: 'Master the foundation of Lumo: staying calm, authentic, and confident when prospects push back.',
      bullet_points: [
        'Maintain the same vocal tone whether prospects agree or object',
        'Never sound defensive, desperate, or over-confident',
        'Your tone should feel like a trusted advisor, not a pushy salesperson',
        'Practice pacing and breathing to control emotional reactions',
        'When nervous, you rush—when confident, you breathe'
      ],
      learning_objectives: [
        'Recognize when your tone shifts (record yourself)',
        'Develop a neutral, warm vocal baseline',
        'Stay steady when a prospect gets angry or dismissive',
        'Use silence confidently instead of filling nervous pauses'
      ],
      example_script: 'Prospect: "This is way too expensive."\nYour response (even tone): "I hear that. Most teams say that initially. What I\'ve found is that when you factor in the time saved, it typically pays for itself in 90 days. Would it be worth 15 minutes to run those numbers together?"',
      pro_tip: 'Record a 2-minute sales call. Listen back. Notice when your tone goes up or gets faster. That\'s where you\'re getting emotional. Practice that section until it feels natural.',
      difficulty: 'Beginner'
    },
    micro_probes: {
      title: 'Micro-Probes: The Art of the Clarifying Question',
      summary: 'Use small, targeted questions to understand the real issue behind an objection.',
      bullet_points: [
        'Micro-probes are 5-10 word questions that dig one level deeper',
        'Instead of "What do you mean by that?" use "Timing—is it Q3 budgets, or hiring?"',
        'Never ask yes/no questions; ask "What" or "How"',
        'Pause for 3+ seconds after they answer (resist the urge to fill silence)',
        'Each probe should reveal ONE piece of information'
      ],
      learning_objectives: [
        'Craft targeted micro-probes for common objections',
        'Avoid multi-part questions that confuse prospects',
        'Master the power pause (silence = thinking)',
        'Turn vague objections into specific, solvable problems'
      ],
      example_script: 'Prospect: "We\'re not sure if this fits our workflow."\nMicro-probe: "Workflow—meaning approval steps, or the day-to-day usage?"\n[They answer]\nNext probe: "Of those, which one\'s the biggest blocker right now?"',
      pro_tip: 'Write 3 micro-probes for your 5 most common objections. Practice them until they sound natural, not scripted. They should feel like a curious consultant, not an interrogation.',
      difficulty: 'Beginner'
    },
    neutral_confidence: {
      title: 'Neutral Confidence: Lead Without Dominating',
      summary: 'Confidence isn\'t about talking more—it\'s about being comfortable with silence and letting the prospect think.',
      bullet_points: [
        'Neutral confidence means you don\'t need validation from the prospect',
        'Over-confidence sounds pushy; under-confidence sounds weak',
        'Confident body language: shoulders back, steady eye contact, no fidgeting',
        'When you genuinely believe in your solution, your tone naturally steadies',
        'Confidence in handling objections is more powerful than avoiding them'
      ],
      learning_objectives: [
        'Distinguish between arrogance and healthy confidence',
        'Use body language to project steadiness (even on video)',
        'Handle "No" without desperation',
        'Ask questions from curiosity, not fear'
      ],
      example_script: 'Instead of: "I know this is perfect for you." (Over-confident)\nSay: "Most teams in your space see a 30% efficiency gain. I\'d love to show you how." (Neutral confidence - you\'re stating a fact, not pushing)',
      pro_tip: 'Confidence comes from preparation. Know your product cold. Know your industry. Know your value. Then relax. You\'ve earned the right to be confident.',
      difficulty: 'Intermediate'
    },
    stacked_objections: {
      title: 'Stacked Objections: Handling Multiple Concerns at Once',
      summary: 'When a prospect throws 3 objections at you simultaneously, stay organized and tackle them systematically.',
      bullet_points: [
        'Write down each objection as they speak (shows you\'re listening)',
        'Acknowledge all of them: "I hear three things: price, timing, and integration."',
        'Ask which one matters most: "Which of these would make the biggest difference?"',
        'Solve the top one completely, then move to the next',
        'Never skip an objection or pretend it wasn\'t raised'
      ],
      learning_objectives: [
        'Organize multiple objections without losing the thread',
        'Prioritize which objections to address first',
        'Handle objections one at a time (not all together)',
        'Get agreement on each before moving to the next'
      ],
      example_script: 'Prospect: "This is expensive, your timeline seems tight, and I\'m not sure it integrates with our Salesforce."\nYour response: "Got it—three things to sort: price, timeline, and Salesforce integration. Which of those is the main thing that would make or break this for you?" [They pick one] "Perfect. Let\'s tackle that first..."',
      pro_tip: 'Stacked objections usually mean the prospect IS interested but has legitimate concerns. This is a GOOD sign. Stay calm and methodical.',
      difficulty: 'Intermediate'
    },
    improvement_loop: {
      title: 'The Improvement Loop: Learning From Every Call',
      summary: 'Mastery comes from reflecting on each call and adjusting your approach based on what worked.',
      bullet_points: [
        'After every call, ask: "What went well? What would I do differently?"',
        'Track which techniques work with which personas',
        'Record calls (with permission) and listen for tone/pacing issues',
        'Review objections and score your responses: Did I stay even-toned? Did I probe?',
        'Celebrate wins—they build confidence for the next call'
      ],
      learning_objectives: [
        'Create a call review framework (5 questions to ask yourself)',
        'Identify patterns in your sales behavior',
        'Build a personal "what works" list',
        'Use data to improve, not just intuition'
      ],
      example_script: 'After-call reflection: "They said budget was the blocker, but when I probed, it was really timing. Next time I hear \'budget,\' I\'ll immediately ask if it\'s the amount or the timing. That micro-probe saved me 10 minutes and got us to the real issue."',
      pro_tip: 'Keep a simple spreadsheet: objection raised → probe you used → outcome. After 50 calls, you\'ll see patterns. Share these insights with your team.',
      difficulty: 'Beginner'
    }
  },
  spin_selling: {
    situation_questions: {
      title: 'Situation Questions: Build Your Context',
      summary: 'Master the first pillar of SPIN: asking about the prospect\'s current state, business, and priorities.',
      bullet_points: [
        'Situation questions establish fact and build your credibility',
        'Ask about their business structure, processes, and current tools',
        'Limit to 3-5 situation questions (too many feel like interrogation)',
        'Listen more than you talk; your goal is understanding',
        'NEVER position your solution yet; just understand'
      ],
      learning_objectives: [
        'Ask situation questions that reveal business context',
        'Avoid defensive situation questions (shows you didn\'t do prep)',
        'Use situation questions to uncover pain points naturally',
        'Know when to move from situation → problem questions'
      ],
      example_script: 'Situation questions for a project management tool sale:\n- "Walk me through how your team currently manages projects."\n- "How many people are involved in a typical project lifecycle?"\n- "What tools are you using right now, and how\'s that working?"',
      pro_tip: 'Do your homework first. If you ask "What industry are you in?" when you already know, you lose credibility. Situation questions should fill gaps in your knowledge, not rehash public info.',
      difficulty: 'Beginner'
    },
    problem_probes: {
      title: 'Problem Probes: Uncover the Real Pain',
      summary: 'Move beyond surface-level facts to discover the difficulties and dissatisfactions the prospect faces.',
      bullet_points: [
        'Problem probes ask about difficulties, frustrations, or weak spots',
        'Examples: "Where do things get difficult?" "What frustrates your team?"',
        'Prospects rarely volunteer problems—you have to ask',
        'Listen for emotional language (that\'s where real pain lives)',
        'Problems = motivation to change'
      ],
      learning_objectives: [
        'Ask open-ended problem probes',
        'Recognize when a prospect hints at a problem',
        'Dig deeper: "Tell me more about that."',
        'Build a list of 5-10 common problems in your industry'
      ],
      example_script: 'After understanding their situation:\n- "Where do things get difficult with your current process?"\n- "What about that frustrates you most?"\n- "How long has that been an issue?"\n[Listen—they\'re revealing pain. Don\'t jump in yet.]',
      pro_tip: 'The best problem probes are industry-specific. For SaaS: "How much time does manual data entry cost you?" For enterprise: "How does this impact your quarterly close process?"',
      difficulty: 'Intermediate'
    },
    implication_building: {
      title: 'Implication Building: Make Problems Matter',
      summary: 'Help the prospect understand the consequences of their problems—this creates urgency.',
      bullet_points: [
        'Problems themselves don\'t drive urgency; consequences do',
        'Use implications to expand a small problem into a big one',
        'Ask "What does that mean for...?" to uncover ripple effects',
        'Connect problems to business outcomes (revenue, time, team morale)',
        'Implications should come FROM the prospect, not from you'
      ],
      learning_objectives: [
        'Ask implication questions that the prospect answers themselves',
        'Guide the prospect to their own realization',
        'Connect day-to-day problems to business impact',
        'Know the "so what?" behind each problem'
      ],
      example_script: 'Problem: "We\'re spending 2 hours per day on manual data entry."\nImplication question: "What does 2 hours per day cost you in productivity?"  [They realize: 10 hours/week = 500 hours/year]\nNext: "And if your team could focus those 10 hours on selling instead, what\'s the revenue impact?"',
      pro_tip: 'Let them do the math. When they calculate the cost themselves, they own the problem. That\'s when they\'re ready to buy.',
      difficulty: 'Advanced'
    },
    need_identification: {
      title: 'Need Identification: Get to "We Need Your Solution"',
      summary: 'Turn implications into explicit needs—where the prospect admits they need a solution.',
      bullet_points: [
        'A need is when a prospect sees value in change',
        'Needs are different from problems (problem = pain, need = wanting to change)',
        'Explicit needs sound like: "We need to automate this." "We should get a tool for that."',
        'Your job is to help them articulate the need themselves',
        'Once they say "We need...", they\'re ready to hear your solution'
      ],
      learning_objectives: [
        'Recognize when a prospect has moved to a need',
        'Ask need-building questions',
        'Summarize the need back to confirm understanding',
        'Know the difference between a want and a need'
      ],
      example_script: 'After implications:\nYou: "So if you could reduce manual data entry, that would help your team focus on more valuable work?"\nProspect: "Absolutely."\nYou: "So you need a system that automates data entry?"\nProspect: "Yes, we definitely need that."  [NEED IDENTIFIED]',
      pro_tip: 'Don\'t pitch until they\'ve said the need out loud. Once they own the problem and the implication, they\'ve sold themselves.',
      difficulty: 'Advanced'
    },
    payoff_establishment: {
      title: 'Payoff Establishment: Why They Should Care',
      summary: 'Ensure the prospect understands the value and benefit of solving the need.',
      bullet_points: [
        'Payoff is the value or benefit of solving a problem',
        'Don\'t assume they know the payoff—make it explicit',
        'Connect features to benefits (feature = what it does, benefit = what it means to them)',
        'Payoff should address their priorities, not yours',
        'Ask: "What would be the benefit to your team?"'
      ],
      learning_objectives: [
        'Translate features into customer-specific benefits',
        'Connect payoff to their stated business priorities',
        'Ask them to articulate the payoff in their own words',
        'Avoid generic benefits; be specific to their situation'
      ],
      example_script: 'Feature: "Our system integrates with Salesforce and automatically pulls data."\nBenefit for them: "That means your sales team spends less time on admin and more time selling. For a team of 5, that\'s 50 hours per week freed up."',
      pro_tip: 'The best payoff is the one they care about, not the one you think is impressive. Ask: "What\'s most important—speed, accuracy, or team bandwidth?" Then emphasize THAT payoff.',
      difficulty: 'Beginner'
    }
  },
  meddic: {
    metrics_definition: {
      title: 'Metrics Definition: Quantify the Problem',
      summary: 'MEDDIC starts with numbers. Identify the key business metrics that matter to the buyer.',
      bullet_points: [
        'Every buyer cares about metrics: revenue, cost, time, capacity, quality',
        'Ask: "What metrics define success for you?"',
        'Know THEIR metrics, not your industry metrics',
        'A manufacturing buyer cares about downtime; a SaaS buyer cares about NPS',
        'Document the baseline (current state) and the target (desired state)'
      ],
      learning_objectives: [
        'Identify 2-3 critical metrics for the buyer',
        'Ask about both leading and lagging indicators',
        'Quantify problems in terms of metrics',
        'Align your solution to their metrics'
      ],
      example_script: 'Enterprise manufacturing:\n- "What metrics do you track for line efficiency?"\n- "What\'s your current downtime cost per hour?"\n- "If we reduced downtime by 15%, what would that mean to your bottom line?"\n[Now you can show ROI]',
      pro_tip: 'In enterprise deals, metrics justify the budget. Without them, you\'re asking someone to spend money on a feeling. With them, you\'re showing ROI.',
      difficulty: 'Intermediate'
    },
    economic_buyer: {
      title: 'Economic Buyer: Find Who Controls the Money',
      summary: 'You can have the best solution, but if the economic buyer doesn\'t see value, the deal dies.',
      bullet_points: [
        'The economic buyer controls budget and final decision authority',
        'They\'re often NOT the person asking for the solution',
        'In large deals, identify the economic buyer early',
        'Build a relationship with them (or you\'ll lose the deal at the end)',
        'The economic buyer cares about metrics and ROI, not features'
      ],
      learning_objectives: [
        'Identify who the economic buyer is (ask directly if unsure)',
        'Understand their priorities and constraints',
        'Build economic buyer engagement early',
        'Never present without economic buyer context'
      ],
      example_script: 'To your champion: "In a decision like this, who would have final sign-off on the budget?"\n[They name the economic buyer]\nYou: "Got it. What matters most to [economic buyer]—speed to implementation, cost, or proven ROI?"',
      pro_tip: 'If your champion isn\'t the economic buyer, stay aligned with the champion but always think about what the economic buyer needs to see to approve.',
      difficulty: 'Intermediate'
    },
    decision_criteria: {
      title: 'Decision Criteria: What Gets You Picked',
      summary: 'Before you pitch, understand what criteria the buyer will use to evaluate solutions.',
      bullet_points: [
        'Ask early: "How will you evaluate solutions?"',
        'Listen for stated criteria (features, price, timeline) and unstated (politics, risk)',
        'Your solution should address their decision criteria, not yours',
        'Get criteria in writing or agreed upon',
        'If you don\'t know the criteria, you can\'t position against competitors'
      ],
      learning_objectives: [
        'Extract decision criteria through questions',
        'Differentiate on criteria that matter to THEM',
        'Address unstated concerns (politics, internal alignment)',
        'Align your strengths to their criteria'
      ],
      example_script: 'To the buying committee:\n"To help me understand how you\'ll evaluate options, what\'s most important—implementation speed, cost, existing integrations, or something else?"\n[They list 5 things. NOW you know how to position.]',
      pro_tip: 'Decision criteria shift during the sales cycle. Check in quarterly: "Has anything changed in how you\'ll evaluate this?" This keeps you aligned.',
      difficulty: 'Intermediate'
    },
    decision_process: {
      title: 'Decision Process: Navigate the Buying Committee',
      summary: 'In enterprise deals, multiple people are involved. Understand the process before you\'re in it.',
      bullet_points: [
        'Ask: "Walk me through how a decision like this gets made."',
        'Understand the stages: evaluation → internal alignment → approval → implementation',
        'Know who needs to sign off at each stage',
        'Build relationships with multiple stakeholders',
        'Speed of approval depends on process clarity'
      ],
      learning_objectives: [
        'Map the buyer\'s internal decision process',
        'Identify who needs to agree at each stage',
        'Anticipate objections from different stakeholders',
        'Navigate politics without forcing alignment'
      ],
      example_script: 'To your champion: "Help me understand the process. Once you decide this is right, what happens next?"\nChampion: "I tell my director, who talks to finance, then we present to the VP."\nYou: "Perfect. What matters most to your director? And to the VP?"',
      pro_tip: 'The decision process is usually 3x longer than the prospect tells you. Plan for delays in getting internal alignment. Stay patient and keep the sponsor warm.',
      difficulty: 'Advanced'
    },
    identify_pain: {
      title: 'Identify Pain: The Real Problem Behind the Request',
      summary: 'Enterprise buyers don\'t reach out because they want your product. They reach out because something hurts.',
      bullet_points: [
        'Pain is the problem driving the need to change',
        'Surface pain: "Our system is outdated." Real pain: "We\'re losing $2M/year to inefficiency."',
        'Ask about impact: "How is that affecting your business?"',
        'Connect pain to metrics (revenue risk, productivity loss, compliance risk)',
        'Pain drives urgency and buying power'
      ],
      learning_objectives: [
        'Distinguish between surface pain and root cause',
        'Ask about business impact, not just process problems',
        'Get the buyer to articulate the pain in their own words',
        'Quantify the pain in business terms'
      ],
      example_script: 'Surface pain: "Our contract management system is slow."\nDigging deeper:\n- "How slow? How long does a contract review take?"\n- "How many deals are delayed because of that?"\n- "What\'s the revenue impact of those delays?"\n[Real pain: $500K in delayed deals per quarter]',
      pro_tip: 'In enterprise, pain is your friend. The bigger the pain, the faster the buy and the less you have to discount.',
      difficulty: 'Intermediate'
    },
    champion_building: {
      title: 'Champion Building: Your Internal Advocate',
      summary: 'A champion is the person inside who believes in your solution and will advocate internally for you.',
      bullet_points: [
        'Your champion needs to benefit from your solution',
        'Provide your champion with tools (ROI docs, comparison charts, talking points)',
        'Regular communication: check in weekly, not monthly',
        'Ask: "What do you need from me to move this forward?"',
        'Protect your champion; they\'re taking political risk recommending you'
      ],
      learning_objectives: [
        'Identify and cultivate the right champion',
        'Provide materials and talking points for their internal pitch',
        'Align with your champion\'s incentives',
        'Navigate corporate politics through your champion'
      ],
      example_script: 'To your champion: "I know you\'re taking this internally. What can I send you—ROI doc, comparison with competitors, customer references—that would help you make the case?"',
      pro_tip: 'Your champion is often NOT the economic buyer. They\'re a user or department head who sees the benefit. Keep them informed and armed with what they need to sell internally.',
      difficulty: 'Intermediate'
    }
  },
  sandler: {
    bonding_rapport: {
      title: 'Bonding and Rapport: Start With Trust',
      summary: 'In Sandler, relationships come first. Before you can sell, you must build genuine rapport.',
      bullet_points: [
        'Spend the first 5-10 minutes on small talk (not rushing to business)',
        'Find common ground (shared experiences, industry perspective, humor)',
        'Show genuine interest in the person, not just the deal',
        'Mirror their communication style (if they\'re detailed, be detailed; if casual, be casual)',
        'Trust is earned, not given'
      ],
      learning_objectives: [
        'Master the art of small talk without being awkward',
        'Build authentic connections, not fake rapport',
        'Read the room and adjust your approach',
        'Establish yourself as someone who cares about them, not the commission'
      ],
      example_script: 'Instead of jumping into your pitch:\n"Before we dive into [solution], I\'d love to know more about your background. How did you end up in this role? What attracted you to the company?"',
      pro_tip: 'Sandler says people buy from people they like. If they like you, objections are easier to handle. If they don\'t, you\'re fighting uphill.',
      difficulty: 'Beginner'
    },
    up_front_contracts: {
      title: 'Up-Front Contracts: Set Expectations Early',
      summary: 'Clarify what\'s going to happen in the conversation and get agreement before you proceed.',
      bullet_points: [
        'An up-front contract is a brief agreement on the purpose and outcome of the call',
        'Examples: "I want to ask some questions to understand your situation. If it\'s a fit, we\'ll explore together. If not, I\'ll be honest."',
        'This prevents surprises and positions you as honest',
        'Get their agreement: "Does that sound fair?"',
        'Revisit the contract if the call changes direction'
      ],
      learning_objectives: [
        'Craft up-front contracts for different call types',
        'Get explicit agreement on the agenda',
        'Use contracts to manage expectations',
        'Establish yourself as someone who operates with integrity'
      ],
      example_script: 'Early in the call:\n"Here\'s what I\'d like to do: I\'ll ask questions to understand your business and priorities. Based on that, I\'ll tell you honestly if I think we can help or if I don\'t think it\'s a fit. Sound okay?"',
      pro_tip: 'Up-front contracts eliminate awkwardness later. If you say upfront "I\'ll be honest if it\'s a fit or not," they trust you. Then when you walk away from a bad deal, they respect you.',
      difficulty: 'Beginner'
    },
    qualification: {
      title: 'Qualification: Is This a Real Opportunity?',
      summary: 'Not every lead is worth your time. Qualify early and often—don\'t waste time on dead deals.',
      bullet_points: [
        'Qualify on need, timeline, authority, and budget (NTAB)',
        'Ask directly: "Do you have the authority to make this decision?"',
        'If they can\'t decide, move on or get them to the decision-maker',
        'A qualified deal has need, authority, and timeline',
        'Unqualified deals waste your time and theirs'
      ],
      learning_objectives: [
        'Ask direct qualification questions without being rude',
        'Identify early disqualifiers (no budget, no authority, wrong fit)',
        'Walk away from dead deals quickly',
        'Respect the prospect\'s time by not chasing fake opportunities'
      ],
      example_script: 'After understanding their situation:\n"Let me ask directly—if this is the right solution, do you have the authority to make the decision? Or would [someone else] need to approve?"\n[If they don\'t have authority] "Got it. Would it make sense to include them now, or should we save time and reconnect when timing\'s better?"',
      pro_tip: 'Qualification is kindness. Walking away from a bad deal tells the prospect you respect their time. That\'s rare and builds trust.',
      difficulty: 'Intermediate'
    },
    pain_discovery: {
      title: 'Pain Discovery: Understand What Hurts',
      summary: 'Sandler focuses on discovering and validating pain—the real reason they need to change.',
      bullet_points: [
        'Pain is emotional. Facts are intellectual. Emotion drives decisions.',
        'Ask about impact: "How is that affecting you?"',
        'Listen for frustration, anger, or resignation—that\'s real pain',
        'Acknowledge the pain: "I can see why that\'s frustrating."',
        'Don\'t rush; let them sit with the pain for a moment'
      ],
      learning_objectives: [
        'Distinguish between problems and pain',
        'Ask questions that surface emotional drivers',
        'Validate pain without fixing it (yet)',
        'Know the difference between surface pain and root cause'
      ],
      example_script: 'Prospect: "Our manual process is inefficient."\nInstead of nodding, dig:\n"What does that inefficiency cost you—in time, money, stress?"\n[They realize the real impact]\nYou: "I can see why that would be frustrating. How long has that been going on?"',
      pro_tip: 'Sandler says once you identify pain, stop talking. Let the prospect sit with it. The discomfort of pain is what drives change.',
      difficulty: 'Intermediate'
    },
    problem_priority: {
      title: 'Problem Priority: Focus on What Matters Most',
      summary: 'Prospects often have multiple problems. Help them identify which one to solve first.',
      bullet_points: [
        'Multiple problems = confused buyer',
        'Ask: "Of these issues, which one—if solved—would make the biggest difference?"',
        'Focus on one main problem',
        'Secondary problems can come later',
        'Solving the wrong problem loses the deal'
      ],
      learning_objectives: [
        'Extract the priority problem from a list',
        'Avoid scope creep by staying focused',
        'Get agreement on which problem to solve first',
        'Address priorities in order of impact'
      ],
      example_script: 'After uncovering pain points:\nYou: "I hear three things: slow process, poor reporting, and team frustration. If you could only fix one, which would matter most?"\nProspect: "The speed—that\'s killing us."\nYou: "Perfect. Let\'s focus there first. Once we solve speed, the other two usually improve too."',
      pro_tip: 'Sandler philosophy: solve the most important problem well, not three problems poorly.',
      difficulty: 'Intermediate'
    },
    emotional_commitment: {
      title: 'Emotional Commitment: They Have to WANT It',
      summary: 'It\'s not enough for a solution to be logical. The prospect must emotionally commit to change.',
      bullet_points: [
        'Logical reasons are necessary but not sufficient',
        'Emotional commitment sounds like: "I\'m excited about this" or "This will change how we work"',
        'Build emotional commitment by connecting solution to their values and goals',
        'If they\'re not emotionally invested, they won\'t fight for budget',
        'Your enthusiasm alone doesn\'t create their commitment'
      ],
      learning_objectives: [
        'Recognize when a prospect has emotional commitment vs. logical agreement',
        'Build emotional commitment through storytelling and vision',
        'Get them to articulate why this matters to them',
        'Address fears or hesitations that block emotional buy-in'
      ],
      example_script: 'Rather than: "This solution will save you 5 hours per week."\nTry: "Imagine your team spending less time on admin and more time on strategy and creativity. What would your team do with that time? What would change?"',
      pro_tip: 'People don\'t buy from logic. They buy from emotion and justify with logic. Build the emotional commitment first.',
      difficulty: 'Advanced'
    },
    post_settlement: {
      title: 'Post-Settlement: Your Deal Isn\'t Done',
      summary: 'The deal doesn\'t close when they say yes. It closes when they say yes to you.',
      bullet_points: [
        'Post-settlement = addressing objections that emerge after verbal agreement',
        'Examples: Legal has concerns, finance wants a discount, implementation is pushed',
        'Stay in touch after the verbal "yes" and before signature',
        'Help them navigate internal objections',
        'Your champion may need you to help them defend the decision internally'
      ],
      learning_objectives: [
        'Anticipate post-settlement objections',
        'Support your champion through internal alignment',
        'Address late-stage concerns with the same respect as early ones',
        'Get the deal properly closed (signed contract) before you leave'
      ],
      example_script: 'After they verbally agree:\n"Excellent. I know you\'re going to socialize this internally. What might be concerns from [finance/legal/IT]? How can I help address those?"\n[Arm them with ammunition for internal conversations]',
      pro_tip: 'Many deals die between "yes" and signature. Stay involved. Call your champion. Ask: "How\'s internal alignment going? What can I do to help?"',
      difficulty: 'Intermediate'
    }
  },
  challenger: {
    teaching: {
      title: 'Teaching: Change How They Think',
      summary: 'Challenger sales is about teaching prospects something they don\'t know—shifting their perspective.',
      bullet_points: [
        'Most prospects don\'t know what they don\'t know',
        'Your job is to educate, not just respond to their questions',
        'Teach insights specific to their industry and situation',
        'Introduce frameworks or data that reframe their understanding',
        'Teaching builds trust and positions you as an expert'
      ],
      learning_objectives: [
        'Research insights specific to their industry',
        'Prepare 2-3 teaching points for the conversation',
        'Share insights that make them say "We never thought about it that way"',
        'Use customer stories and data to support your teaching'
      ],
      example_script: 'Teaching moment:\n"I work with a lot of companies in your space. The ones scaling fastest aren\'t optimizing for cost—they\'re optimizing for team focus. Here\'s what I mean: when you move [process] off the team\'s plate, they deliver 40% more revenue. Let me show you how."',
      pro_tip: 'Spend 30% of your prep time on teaching points. What do you know about their industry that they might not? That\'s your teaching edge.',
      difficulty: 'Advanced'
    },
    tailoring: {
      title: 'Tailoring: Make It About Them',
      summary: 'Tailor your message, your solution, and your approach to their unique situation.',
      bullet_points: [
        'Generic solutions feel generic. Custom approaches feel valuable.',
        'Reference their specific business, metrics, and challenges',
        'Show that you understand their world, not just your product',
        'Customize your proposal to their priorities (not your defaults)',
        'Tailoring says: "I did research. You matter."'
      ],
      learning_objectives: [
        'Research their company, industry, and recent changes',
        'Reference specifics in every conversation',
        'Customize your value prop to their situation',
        'Show you understand their competitive landscape'
      ],
      example_script: 'Generic: "This improves efficiency."\nTailored: "Given that you\'re in the [industry], where margins are [X], and you\'re facing [specific competitor], an efficiency gain translates directly to competitive advantage. Here\'s specifically how."',
      pro_tip: 'Tailoring requires prep. Spend 15 minutes before each call reviewing their website, recent news, and financial reports. That prep pays off in trust.',
      difficulty: 'Intermediate'
    },
    taking_control: {
      title: 'Taking Control: Lead the Conversation',
      summary: 'Don\'t let prospects drive the conversation into rabbit holes. Guide them toward valuable outcomes.',
      bullet_points: [
        'Taking control means redirecting conversations strategically',
        'Example: If they ask "Do you integrate with X?" redirect: "Good question—what\'s your integration priority?"',
        'Set the agenda early: "Here\'s what I\'d like to explore..."',
        'Use transitions: "That\'s important. And what I\'ve found is..."',
        'Control ≠ arrogance. It\'s respectful leadership'
      ],
      learning_objectives: [
        'Recognize when a conversation is getting off track',
        'Redirect with questions, not statements',
        'Maintain control without being pushy',
        'Guide toward closure without being aggressive'
      ],
      example_script: 'Prospect: "Do you integrate with 47 different tools?"\nInstead of: "Yes, we do." [End of conversation]\nSay: "We integrate with most major platforms. What\'s most important to connect—your CRM, your BI tool, or something else?"\n[Now you\'re understanding priorities, not just listing features]',
      pro_tip: 'Taking control is about respecting the prospect\'s time enough to guide them toward a real decision. Weak sales reps let prospects ramble.',
      difficulty: 'Advanced'
    }
  },
  objection_mastery: {
    price_objections: {
      title: 'Mastering Price Objections',
      summary: 'Price objections are the most common. Master these and you\'ll close more deals.',
      bullet_points: [
        'Price objections are often disguised budget objections (not about cost, about priority)',
        'Ask: "Is price the real issue, or is it about priority?" (Often it\'s priority)',
        'Never cut price without understanding what they\'re comparing to',
        'Use ROI: "This costs $10K but saves $50K per year. Can you afford NOT to do it?"',
        'If price is real, pause the deal. Desperation shows.'
      ],
      learning_objectives: [
        'Distinguish between real price objections and buying signals',
        'Handle price without discounting reflexively',
        'Use ROI to justify price',
        'Know your walk-away price point'
      ],
      example_script: '"Your solution costs $50K, but competitor X is $30K."\nYou: "That\'s fair. What\'s included in their $30K?"\n[They realize competitor is missing features]\nYou: "So it\'s not apples to apples. Let me show you the real value delivered for the investment."',
      pro_tip: 'In enterprise, if price is their only objection, you\'ve won. Price objections mean they\'re serious—they\'re just fighting internal budget approval.',
      difficulty: 'Intermediate'
    },
    timing_objections: {
      title: 'Mastering Timing Objections',
      summary: '"Not now" is often "not convinced yet." Understand the real timing concern.',
      bullet_points: [
        'Timing objections sound like: "Not in budget right now" or "Maybe next quarter"',
        'Ask: "Is it timing, or is it about fit?" (Often it\'s fit)',
        'If truly timing: "What would need to happen for this to move up? When should I follow up?"',
        'Calendar the follow-up and stick to it',
        'Timing can be a real objection (budget cycle) or an excuse (not convinced)'
      ],
      learning_objectives: [
        'Identify real timing vs. polite rejection',
        'Ask clarifying questions about timing',
        'Set a realistic follow-up timeline',
        'Use timing objections to build urgency (when justified)'
      ],
      example_script: '"We love your solution, but we don\'t have budget until Q3."\nYou: "Got it. When is your budget planning process? If we start the conversation now, you could have budget approved before Q3 starts. What would help you move faster?"',
      pro_tip: 'If they say "not now," ask why now isn\'t the time. Often you\'ll uncover the real issue. If it\'s truly budget cycle, put them in a sequence and come back at the right time.',
      difficulty: 'Intermediate'
    },
    fit_objections: {
      title: 'Mastering Fit Objections',
      summary: '"It doesn\'t fit our workflow" is the hardest objection because it questions your solution\'s core value.',
      bullet_points: [
        'Fit objections mean you haven\'t understood their workflow deeply enough',
        'Ask: "Walk me through exactly how you\'d use this"',
        'Listen for the real concern: Is it integration? Is it change management? Is it complexity?',
        'Either customize your approach or disqualify',
        'Don\'t force a fit if there\'s no fit'
      ],
      learning_objectives: [
        'Dig into their actual workflow and process',
        'Understand their concerns about fit',
        'Show how your solution adapts to their workflow (or admit it doesn\'t)',
        'Know when to walk away from a bad fit'
      ],
      example_script: '"It doesn\'t fit how our team works."\nYou: "I hear you. Help me understand your workflow. Walk me through a typical day using [your current tool]."  \n[Listen to their process]\nYou: "I see. Here\'s how [solution] would integrate into that flow. Here\'s what would change and why. Does that work for you?"',
      pro_tip: 'Fit objections are often valid. Don\'t fight them. Either genuinely adapt your approach or tell them it\'s not the right fit. Respecting misfit builds long-term trust.',
      difficulty: 'Advanced'
    },
    competitor_objections: {
      title: 'Mastering Competitor Objections',
      summary: 'They\'re comparing you to another vendor. Help them compare apples to apples.',
      bullet_points: [
        'Ask: "What\'s important about [competitor]?"',
        'Then: "Where do you see differences between us?"',
        'Don\'t trash-talk competitors. Highlight YOUR strengths instead.',
        'Use data: "On speed, we\'re 3x faster. On integration, we support 50+ platforms."',
        'Acknowledge where competitors are strong, differentiate on what matters to them'
      ],
      learning_objectives: [
        'Understand their competitive considerations without being defensive',
        'Differentiate on criteria that matter to THEM (not what matters to you)',
        'Use competitive frameworks to guide comparison',
        'Know your competitive advantages cold'
      ],
      example_script: '"Competitor X does something similar."\nYou: "They do. What drew you to them?"\n[They answer]\nYou: "Totally fair. Where I think we differ is [X] and [Y]. Given your priorities around [their priority], which matters more?"',
      pro_tip: 'Competitive objections are validation that they\'re serious about solving the problem. Turn it into differentiation, not defensiveness.',
      difficulty: 'Intermediate'
    },
    budget_objections: {
      title: 'Mastering Budget Objections',
      summary: 'Often the last objection before the deal closes. Handle it with strategic thinking.',
      bullet_points: [
        'Budget objections come in three forms: no budget, budget allocated elsewhere, budget too small',
        'Ask: "If this ROI is real, is budget the real issue or the commitment?"',
        'For no budget: "What would need to happen to free up budget? Whose buy-in do we need?"',
        'For allocated elsewhere: "What if I show you this delivers more value than [current spend]?"',
        'For budget too small: "Would a phased approach work?"'
      ],
      learning_objectives: [
        'Identify the real nature of the budget objection',
        'Show ROI that justifies budget reallocation',
        'Propose payment terms or phased approaches',
        'Know when budget is the real issue vs. a smokescreen'
      ],
      example_script: '"We don\'t have $50K budgeted for this."\nYou: "Understood. If I showed you this saves $150K in labor, is budget the barrier or is it about justifying a new expense category?"\n[If it\'s justifying]: "Let me help you build the business case to reallocate budget from [lower ROI area]."',
      pro_tip: 'Budget objections, when ROI is clear, often mean they need political cover to justify the spend. Help them build the case internally.',
      difficulty: 'Advanced'
    }
  },
  nepq: {
    connection_phase: {
      title: 'Connection Phase: Lower Resistance',
      summary: 'Start with disarming, non-threatening questions that reduce sales pressure and open the conversation.',
      bullet_points: [
        'Use neutral, curious phrasing ("Just so I understand...")',
        'Avoid pitching—focus on their situation and goals',
        'Ask permission to ask questions and explore',
        'Calm tonality: slow, steady, respectful'
      ],
      learning_objectives: [
        'Open calls with disarming questions',
        'Use permission-based transitions',
        'Set a collaborative frame early'
      ],
      example_script: '"Would it be okay if I asked a few questions to understand your situation before I jump into anything?"',
      pro_tip: 'Your first 30 seconds determine resistance level. Never sound like you\'re \'selling.\'',
      difficulty: 'Beginner'
    },
    situation_phase: {
      title: 'Situation Phase: Understand Context',
      summary: 'Gather context without interrogation. Keep questions short, clear, and purpose-driven.',
      bullet_points: [
        'Clarify current process, tools, and team structure',
        'Ask how they decided to explore change now',
        'Surface hidden constraints early (timeline, compliance, contracts)'
      ],
      learning_objectives: [
        'Map current state without overwhelming the buyer',
        'Spot decision drivers and constraints',
        'Sequence situation → problem questions naturally'
      ],
      example_script: '"How are you handling this today, and what prompted you to look at this now?"',
      pro_tip: 'Limit to 3–5 strong situation questions. Save depth for problem discovery.',
      difficulty: 'Beginner'
    },
    problem_awareness: {
      title: 'Problem Awareness: Reveal the Real Issues',
      summary: 'Turn vague frustrations into clear, solvable problems through precision questioning.',
      bullet_points: [
        'Ask: "What\'s not working with the current approach?"',
        'Clarify scope and frequency of the problem',
        'Identify who is most impacted and how'
      ],
      learning_objectives: [
        'Separate symptoms from root problems',
        'Capture impact by role and process',
        'Prioritize problems based on business effect'
      ],
      example_script: '"When that happens, how often, and what does it cause downstream?"',
      pro_tip: 'Problems become motivation only when impact is clear and agreed upon.',
      difficulty: 'Intermediate'
    },
    consequence_questions: {
      title: 'Consequence Questions: Build Urgency',
      summary: 'Guide prospects to articulate the cost of inaction in their own words.',
      bullet_points: [
        'Ask second-order effects: time, revenue, risk, morale',
        'Have them quantify impact wherever possible',
        'Use silence after questions—let them think'
      ],
      learning_objectives: [
        'Link problems to meaningful business impact',
        'Elicit quantified consequences',
        'Use pauses to deepen reflection'
      ],
      example_script: '"What happens if this isn\'t solved in the next quarter? What would that cost?"',
      pro_tip: 'If they say the consequence, they own the urgency. Don\'t over-explain.',
      difficulty: 'Advanced'
    },
    commitment_questions: {
      title: 'Commitment Questions: Secure Next Steps',
      summary: 'Move from insight to action with low-friction commitments that advance the deal.',
      bullet_points: [
        'Ask for micro-commitments (data, access, internal intro)',
        'Tie commitment to value discovered',
        'Confirm who, what, when—make it concrete'
      ],
      learning_objectives: [
        'Craft natural commitment asks',
        'Sequence commitments by stage',
        'Avoid premature big asks'
      ],
      example_script: '"Would it make sense to loop in finance so we can validate the ROI we discussed?"',
      pro_tip: 'Momentum stalls without commitments. Always leave with one.',
      difficulty: 'Intermediate'
    }
  },
  gap_selling: {
    current_state: {
      title: 'Current State: Establish Reality',
      summary: 'Document how things work today in measurable terms.',
      bullet_points: ['Baseline metrics', 'Process map', 'Who does what'],
      learning_objectives: ['Capture current KPIs', 'Identify friction points'],
      example_script: '"Walk me through how this is handled today, step by step."',
      pro_tip: 'Start with facts before opinions.',
      difficulty: 'Beginner'
    },
    future_state: {
      title: 'Future State: Define Success',
      summary: 'Clarify what good looks like and why it matters.',
      bullet_points: ['Target metrics', 'Business outcomes', 'Non-negotiables'],
      learning_objectives: ['Elicit desired outcomes', 'Align on value targets'],
      example_script: '"If this worked perfectly, what would be better—and how would you measure it?"',
      pro_tip: 'Specific futures create clear value arguments.',
      difficulty: 'Beginner'
    },
    gap_quantification: {
      title: 'Quantify the Gap',
      summary: 'Translate the distance between current and future into dollars, time, and risk.',
      bullet_points: ['Compute delta', 'Tie to cost centers', 'Estimate ROI'],
      learning_objectives: ['Build a quantified gap', 'Support with assumptions'],
      example_script: '"You\'re at 65% now and need 90%. That\'s a 25-point gap—what does each point represent in revenue?"',
      pro_tip: 'Numbers drive urgency and budget.',
      difficulty: 'Intermediate'
    },
    root_cause: {
      title: 'Root Cause: Fix the Right Thing',
      summary: 'Move beyond symptoms to the systemic drivers.',
      bullet_points: ['5-Whys', 'Process bottlenecks', 'Ownership gaps'],
      learning_objectives: ['Isolate root causes', 'Map cause → effect'],
      example_script: '"What causes that delay—tools, handoffs, or incentives?"',
      pro_tip: 'Wrong root cause = failed project.',
      difficulty: 'Advanced'
    },
    business_impact: {
      title: 'Business Impact: Executive Relevance',
      summary: 'Tie the gap to executive-level priorities and metrics.',
      bullet_points: ['Revenue', 'Gross margin', 'Compliance/Risk'],
      learning_objectives: ['Executive narrative', 'Board-level framing'],
      example_script: '"If this reduces churn 1%, what\'s the annualized revenue impact?"',
      pro_tip: 'Impact earns economic buyer attention.',
      difficulty: 'Intermediate'
    }
  },
  straight_line: {
    tonality_mastery: {
      title: 'Tonality Mastery',
      summary: 'Use tonal patterns to signal certainty, curiosity, and empathy without sounding salesy.',
      bullet_points: ['Up-tone for curiosity', 'Down-tone for certainty', 'Even-tone for neutrality'],
      learning_objectives: ['Match tone to intent', 'Avoid defensive tones'],
      example_script: 'Curious: "How are you handling this today?"  Certain: "Here\'s what teams do when this matters."',
      pro_tip: 'Record and grade your tonality on key questions.',
      difficulty: 'Beginner'
    },
    certainty_framework: {
      title: 'Certainty in Three Dimensions',
      summary: 'Increase certainty in you, your company, and your solution to drive decisions.',
      bullet_points: ['Personal credibility', 'Company proof', 'Solution fit'],
      learning_objectives: ['Sequence certainty drivers', 'Avoid over-talking certainty'],
      example_script: '"Here\'s why teams trust us (company), what that meant for [peer] (solution), and how I\'ll personally make this easy (you)."',
      pro_tip: 'Certainty should feel calm, not cocky.',
      difficulty: 'Intermediate'
    }
  },
  value_selling: {
    value_hypothesis: {
      title: 'Value Hypothesis',
      summary: 'State a testable hypothesis of value tailored to their situation.',
      bullet_points: ['Industry benchmark', 'Persona context', 'Assumption transparency'],
      learning_objectives: ['Draft hypothesis', 'Validate fast'],
      example_script: '"Given your [team size/process], we estimate a [22–28%] cycle time reduction within 60 days."',
      pro_tip: 'A value hypothesis invites collaboration, not debate.',
      difficulty: 'Beginner'
    },
    stakeholder_value: {
      title: 'Stakeholder Value Mapping',
      summary: 'Map value by stakeholder—finance, ops, IT, end users.',
      bullet_points: ['Role-specific outcomes', 'Conflicts to resolve', 'Shared wins'],
      learning_objectives: ['Align value across roles', 'Preempt objections'],
      example_script: '"For finance it\'s cash efficiency; for ops it\'s throughput; for IT it\'s stability."',
      pro_tip: 'One story won\'t fit all stakeholders.',
      difficulty: 'Intermediate'
    },
    quantify_value: {
      title: 'Quantify Value',
      summary: 'Turn outcomes into quantified benefits and ROI ranges.',
      bullet_points: ['Ranges over point estimates', 'Tie to CFO language', 'Sensitivity analysis'],
      learning_objectives: ['Build value model', 'Pressure-test assumptions'],
      example_script: '"Even at the low end (12% improvement), the annualized impact is $480k."',
      pro_tip: 'Range-based numbers build trust.',
      difficulty: 'Intermediate'
    },
    proof_and_risk: {
      title: 'Proof and Risk Mitigation',
      summary: 'Balance strong proof with a clear plan to manage risk.',
      bullet_points: ['Relevant case studies', 'Pilot plans', 'Exit criteria'],
      learning_objectives: ['Pair proof with safety nets', 'Design sensible pilots'],
      example_script: '"30-day pilot, success criteria agreed, opt-out if criteria not met."',
      pro_tip: 'Risk plans accelerate approvals.',
      difficulty: 'Beginner'
    },
    business_case: {
      title: 'Executive Business Case',
      summary: 'Summarize the why, what, and ROI in one clear narrative.',
      bullet_points: ['Problem → Impact → Solution → ROI', 'Timeline & owner', 'Next decision'],
      learning_objectives: ['Build exec-ready one-pager', 'Align to buying process'],
      example_script: '"We\'re seeking approval for phase 1 (60 days) to capture $220k in-year value with minimal risk."',
      pro_tip: 'Executives decide fast when cases are clear.',
      difficulty: 'Advanced'
    }
  }
};

// Proprietary Unified Framework: make all methods feel cohesive
// Phases: Connect → Diagnose → Quantify → Align → Commit
megaTrainerService.UNIFIED_FRAMEWORK = {
  phases: [
    {
      id: 'connect',
      name: 'Connect',
      purpose: 'Build trust, set the frame, and reduce resistance',
      includes: [
        { module: 'lumo_core', pillars: ['even_tone_authenticity', 'neutral_confidence'] },
        { module: 'sandler', pillars: ['bonding_rapport', 'up_front_contracts'] },
        { module: 'nepq', pillars: ['connection_phase'] },
        { module: 'straight_line', pillars: ['tonality_mastery'] }
      ]
    },
    {
      id: 'diagnose',
      name: 'Diagnose',
      purpose: 'Reveal problems, causes, and priorities',
      includes: [
        { module: 'spin_selling', pillars: ['situation_questions', 'problem_probes'] },
        { module: 'sandler', pillars: ['qualification', 'pain_discovery', 'problem_priority'] },
        { module: 'nepq', pillars: ['situation_phase', 'problem_awareness'] },
        { module: 'gap_selling', pillars: ['current_state', 'root_cause'] }
      ]
    },
    {
      id: 'quantify',
      name: 'Quantify',
      purpose: 'Turn insight into business impact and urgency',
      includes: [
        { module: 'spin_selling', pillars: ['implication_building', 'need_identification', 'payoff_establishment'] },
        { module: 'gap_selling', pillars: ['future_state', 'gap_quantification', 'business_impact'] },
        { module: 'nepq', pillars: ['consequence_questions'] },
        { module: 'value_selling', pillars: ['quantify_value'] }
      ]
    },
    {
      id: 'align',
      name: 'Align',
      purpose: 'Tailor solution, build certainty, and address risk',
      includes: [
        { module: 'challenger', pillars: ['teaching', 'tailoring'] },
        { module: 'straight_line', pillars: ['certainty_framework'] },
        { module: 'value_selling', pillars: ['value_hypothesis', 'stakeholder_value', 'proof_and_risk'] },
        { module: 'objection_mastery', pillars: ['fit_objections', 'competitor_objections'] }
      ]
    },
    {
      id: 'commit',
      name: 'Commit',
      purpose: 'Secure agreement with clear next steps and accountability',
      includes: [
        { module: 'nepq', pillars: ['commitment_questions'] },
        { module: 'sandler', pillars: ['emotional_commitment', 'post_settlement'] },
        { module: 'value_selling', pillars: ['business_case'] }
      ]
    }
  ]
};

// Helpers to navigate across the unified model
megaTrainerService.getLessonIndexByPillar = function(moduleId, pillarName) {
  const pillars = this.TRAINING_MODULES?.[moduleId]?.pillars || [];
  let idx = pillars.indexOf(pillarName);
  if (idx < 0) {
    // Try reverse alias mapping to locate closest pillar index
    const reverseAlias = {
      challenger: {
        teaching: 'insight_teaching',
        tailoring: 'solution_tailoring',
        taking_control: 'taking_control'
      },
      objection_mastery: {
        price_objections: 'objection_types',
        timing_objections: 'acknowledge_pivot',
        competitor_objections: 'frame_flipping',
        budget_objections: 'stacked_handling',
        fit_objections: 'persona_adaptation'
      }
    };
    const map = reverseAlias[moduleId] || {};
    const mapped = map[pillarName];
    if (mapped) idx = pillars.indexOf(mapped);
  }
  return idx >= 0 ? idx + 1 : 1;
};

megaTrainerService.getPhaseFor = function(moduleId, pillarName) {
  const phases = this.UNIFIED_FRAMEWORK?.phases || [];
  for (const phase of phases) {
    for (const inc of phase.includes || []) {
      if (inc.module === moduleId && (inc.pillars || []).includes(pillarName)) {
        return phase;
      }
    }
  }
  return null;
};

megaTrainerService.getRelatedLessons = function(moduleId, pillarName, limit = 4) {
  const phase = this.getPhaseFor(moduleId, pillarName);
  if (!phase) return [];

  const related = [];
  for (const inc of phase.includes || []) {
    if (inc.module === moduleId) continue;
    const mod = this.TRAINING_MODULES?.[inc.module];
    if (!mod) continue;

    const pickPillar = (inc.pillars && inc.pillars[0]) || (mod.pillars && mod.pillars[0]);
    if (!pickPillar) continue;
    const lessonNumber = this.getLessonIndexByPillar(inc.module, pickPillar);
    related.push({
      moduleId: inc.module,
      moduleName: mod.name,
      pillar: pickPillar,
      lessonNumber,
      path: `/app/trainer/${inc.module}/lesson/${lessonNumber}`
    });
    if (related.length >= limit) break;
  }
  return related;
};

export default megaTrainerService;
